import { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { PasswordField } from '@aws-amplify/ui-react';
import { signUp, confirmSignUp } from 'aws-amplify/auth';
import { CashCoin, ShieldCheck, GraphUpArrow, ArrowRight } from 'react-bootstrap-icons';
import ScrollAnimation from 'react-animate-on-scroll';

const benefits = [
  {
    icon: CashCoin,
    title: 'Get paid per lead',
    text: 'Earn every time a legal professional purchases a lead you submitted.',
  },
  {
    icon: GraphUpArrow,
    title: 'Track everything',
    text: 'See your submissions, sales, and earnings in one dashboard.',
  },
  {
    icon: ShieldCheck,
    title: 'Secure payouts',
    text: 'Verified professionals, reliable payments, no surprises.',
  },
];

const passwordRegex = /^(.{0,7}|[^0-9]*|[^A-Z]*|[^a-z]*|[a-zA-Z0-9]*)$/;
const passwordError = 'Requires uppercase, lowercase, and number with a minimum of 8 chars';

export function EarnSection() {
  const [step, setStep] = useState('form'); // form | verify | done
  const [validated, setValidated] = useState(false);
  const [form, setForm] = useState({});
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSignUp = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setValidated(true);
    setError(null);

    const valid =
      event.currentTarget.checkValidity() &&
      password &&
      !passwordRegex.test(password) &&
      confirmPassword === password;
    if (!valid) return;

    try {
      setSubmitting(true);
      await signUp({
        username: form.email,
        password,
        options: {
          userAttributes: {
            name: form.name,
            email: form.email,
          },
        },
      });
      setStep('verify');
    } catch (err) {
      setError(err.message || 'Failed to sign up. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    setError(null);
    try {
      setSubmitting(true);
      await confirmSignUp({ username: form.email, confirmationCode: code.trim() });
      setStep('done');
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="qmi-earn qmi-section" id="earn">
      <div className="qmi-cta-bg"></div>
      <Container className="position-relative">
        <Row className="align-items-center g-5">
          <Col lg={6}>
            <ScrollAnimation animateIn="fadeInLeft" animateOnce={true}>
              <div className="qmi-earn-intro">
                <span className="qmi-section-label">Earn With Us</span>
                <h2 className="qmi-cta-title racing-sans-one-regular">
                  Know Someone Who Was Injured?
                </h2>
                <p className="qmi-earn-subtitle">
                  Submit their case to our network of attorneys and get paid when it sells.
                  Free to join, takes two minutes.
                </p>
              </div>
              <div className="qmi-earn-benefits">
                {benefits.map((b) => (
                  <div key={b.title} className="qmi-earn-benefit">
                    <div className="qmi-earn-benefit-icon">
                      <b.icon size={22} />
                    </div>
                    <div>
                      <div className="qmi-earn-benefit-title">{b.title}</div>
                      <div className="qmi-earn-benefit-text">{b.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollAnimation>
          </Col>

          <Col lg={6}>
            <ScrollAnimation animateIn="fadeInRight" animateOnce={true}>
              <div className="qmi-earn-card">
                {step === 'form' && (
                  <>
                    <h4 className="mb-1">Create your account</h4>
                    <p className="text-muted mb-4">Start submitting leads today.</p>
                    {error && (
                      <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>
                    )}
                    <Form onChange={handleChange} noValidate validated={validated} onSubmit={handleSignUp}>
                      <Form.Group controlId="earn-name" className="mb-3">
                        <Form.Label>Full Name</Form.Label>
                        <Form.Control name="name" required type="text" placeholder="John Smith" />
                        <Form.Control.Feedback type="invalid">Please enter your name.</Form.Control.Feedback>
                      </Form.Group>
                      <Form.Group controlId="earn-email" className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control name="email" required type="email" placeholder="john@example.com" />
                        <Form.Control.Feedback type="invalid">Please enter a valid email.</Form.Control.Feedback>
                      </Form.Group>
                      <Row className="mb-4">
                        <Col md={6}>
                          <PasswordField
                            width="100%"
                            label="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            hasError={password && passwordRegex.test(password)}
                            errorMessage={passwordError}
                          />
                        </Col>
                        <Col md={6}>
                          <PasswordField
                            width="100%"
                            label="Confirm"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            hasError={confirmPassword && password !== confirmPassword}
                            errorMessage="Passwords must match"
                          />
                        </Col>
                      </Row>
                      <Button className="qmi-btn-accent w-100" size="lg" type="submit" disabled={submitting}>
                        {submitting ? 'Creating account...' : 'Create Account'}
                        {!submitting && <ArrowRight size={18} className="ms-2" />}
                      </Button>
                    </Form>
                  </>
                )}

                {step === 'verify' && (
                  <>
                    <h4 className="mb-1">Check your email</h4>
                    <p className="text-muted mb-4">
                      We sent a verification code to <strong>{form.email}</strong>.
                    </p>
                    {error && (
                      <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>
                    )}
                    <Form onSubmit={handleVerify}>
                      <Form.Group controlId="earn-code" className="mb-4">
                        <Form.Label>Verification Code</Form.Label>
                        <Form.Control
                          type="text"
                          inputMode="numeric"
                          value={code}
                          onChange={(e) => setCode(e.target.value)}
                          placeholder="123456"
                          required
                        />
                      </Form.Group>
                      <Button className="qmi-btn-accent w-100" size="lg" type="submit" disabled={submitting || !code}>
                        {submitting ? 'Verifying...' : 'Verify'}
                      </Button>
                    </Form>
                  </>
                )}

                {step === 'done' && (
                  <div className="text-center py-3">
                    <ShieldCheck size={56} className="text-success mb-3" />
                    <h4 className="mb-2">You're all set</h4>
                    <p className="text-muted mb-4">Your account is verified. Sign in to submit your first lead.</p>
                    <Button className="qmi-btn-accent w-100" size="lg" href="/dashboard">
                      Sign In
                      <ArrowRight size={18} className="ms-2" />
                    </Button>
                  </div>
                )}
              </div>
            </ScrollAnimation>
          </Col>
        </Row>
      </Container>
    </section>
  );
}
